/* ==========================================================================
   BOUNTEOUS CONFIDENTIAL
   ___________________

   Copyright 2019 Bounteous
   All Rights Reserved.

   NOTICE:  All information contained herein is, and remains the property
   of Bounteous and its suppliers, if any. The intellectual and
   technical concepts contained herein are proprietary to Bounteous
   and its suppliers and are protected by trade secret or copyright law.
   Dissemination of this information or reproduction of this material
   is strictly forbidden unless prior written permission is obtained
   from Bounteous.
   ========================================================================== */
import { ROCKWELL_BREAKPOINTS_MAX_EM } from 'js/constants.js';

let uniqueCounter = 0;

/**
 * Characters
 */
export const characters = {
    charactersAlpha: 'a b c d e f g h i j k l m n o p q r s t u v w x y z'.split(' '),
    charactersNumeric: '0 1 2 3 4 5 6 7 8 9'.split(' '),
};

/**
 * Millisecond Common Values
 */
export const millis = {
    day: 86400000,
    hour: 3600000,
    minute: 60000,
    second: 1000,
    week: 604800000,
};

/**
 * Common Regex Patterns
 */
export const regexPatterns = {
    /*eslint-disable*/
    regexAbsoluteHttpUrl: /^(https?:)?\/\//i,
    regexBlank: /[^\s]+/,
    regexBlankHtml: /[^(\s|\&(nbsp|emsp);|\<br\ *\/?\>|\u2003)]+/i,
    regexHttpProtocol: /^(https?:)/i,
    regexLtrim: /^\s\s*/,
    regexRtrim: /\s\s*$/,
    regexNumber: /^[-]?(([0-9]+[\.]?[0-9]*)|([0-9]*[\.]?[0-9]+))$/,
    regexPlus: /\+/g,
    regexQueryStrKeyValue: /([^&=]+)(=?)([^&]*)/g,
    regexRegexp: /[-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g,
    regexThousand: /(\d+)(\d{3})/,
    regexUrlParse: /^(?:((?:([^:\/?#\.]+):)?(?:\/\/(?:(?:(?:([^:@]*):?([^:@]*))?@)?([^:\/?#]*)(?::(\d*))?))?(?:\/?([^?#]*)))((?:\?([^#]*)))?((?:#(.*)))?)/,
    safeHtmlRegex: /([<>"'])/g,
    /*eslint-enable*/
};

/**
 * Add Commas
 * @param {number} num - number to add commas to
 * @returns {number}
 */
export function addCommas(num) {
    if (num !== null && typeof num !== 'undefined' && typeof num === 'number') {
        const nStr = num.toString();
        const numParts = nStr.split('.');
        let wholePart = numParts[0];
        const decimalPart = numParts.length > 1 ? `.${numParts[1]}` : '';
        let prevVal;
        do {
            prevVal = wholePart;
            wholePart = wholePart.replace(regexPatterns.regexThousands, '$1,$2');
        } while (prevVal !== wholePart);
        return wholePart + decimalPart;
    }
    return num;
}

/**
 * Add Query Param
 * @param {string} url - url to apply query string to
 * @param {string} paramName - query param key
 * @param {string} paramValue - query param value
 * @returns {string} - url string is returned
 */
export function addQueryParam(url, paramName, paramValue) {
    if(typeof url !== 'string'
        || (typeof paramName !== 'string' && typeof paramName !== 'number')
        || (typeof paramValue !== 'string' && typeof paramValue !== 'number')
    ) return url;

    if (url !== null && paramName !== null && paramValue !== null) {
        const urlParts = parseUrl(url);
        if (urlParts.queryParams[paramName] === undefined) {
            urlParts.queryParams[paramName] = paramValue;
        }
        let newUrl = `${urlParts.path}?${buildQueryString(urlParts.queryParams)}`;
        if (urlParts.hash !== null) {
            newUrl += `#${urlParts.hash}`;
        }
        return newUrl;
    }
    return url;
}

/**
 * Build Query String from Object
 * @param {object} kvPairs - object to build string from
 * @returns {string}
 */
export function buildQueryString(kvPairs) {
    if(isEmptyObject(kvPairs) || kvPairs === null) return '';

    const queryStrItems = [];
    Object.keys(kvPairs).forEach((key) => {
        const val = kvPairs[key];
        if (val === null || (typeof val !== 'object' && typeof val !== 'function')) {
            let str = encodeURIComponent(key);
            if (val !== null) str += `=${encodeURIComponent(val)}`;

            queryStrItems.push(str);
        }
    });

    return queryStrItems.join('&');
}

/**
 * Debounce Handler
 * @param {function} func
 * @param {number} wait
 */
export function debounce(func, wait = 300) {
    if(typeof func !== 'function') return false;

    let timeout;
    return function debounceCallback(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            func.apply(this, args);
        }, wait);
    };
}

/**
 * Get Current URL's HTTP Protocol
 * @returns {string}
 */
export function getHttpProtocol() {
    return document.location.protocol === 'https:' ? 'https:' : 'http:';
}

/**
 * Get a unique counter value
 *
 * The value increments each time this function is called.
 * @returns {number}
 */
export function getNextUniqueCounter() {
    uniqueCounter += 1;
    return uniqueCounter;
}

/**
 * Get URL Param from Current URL
 * @param {string || number} name - name of param to get
 * @returns {string}
 */
export function getUrlParameter(name) {
    if((typeof name !== 'string' && typeof name !== 'number') || name === null) return '';
    const { queryString } = parseUrl(window.location.href);
    return parseQueryString(queryString)[name];
}

/**
 * Checks if URL is Absolute
 * @param {string} s - URL to check
 * @returns {boolean}
 */
export function isAbsoluteHttpUrl(s) {
    if(typeof s !== 'string' || s === null) return false;
    return regexPatterns.regexAbsoluteHttpUrl.test(s);
}

/**
 * Checks for blank string
 * @param {string} s - string to check
 * @returns {boolean}
 */
export function isBlank(s) {
    if(typeof s === 'undefined' || typeof s !== 'string' || s === null) return true;
    return isEmpty(s) || !regexPatterns.regexBlank.test(s);
}

/**
 * Checks if HTML is Blank
 * @param {string} s - HTML string to check
 * @returns {boolean}
 */
export function isBlankHtml(s) {
    if(typeof s !== 'string') return true;
    return isEmpty(s) || !regexPatterns.regexBlankHtml.test(s);
}

/**
 * Checks if in Edit Mode
 * @returns {boolean}
 */
export function isEditModeFooter() {
    return isEditMode();
}

/**
 * Checks if Element is in Viewport
 * @param {DOM Element} el - element
 * @returns {boolean}
 */
export function isElementInViewport(el) {
    if(el === null || typeof el !== 'object') return false;

    let element = el;
    let top = element.offsetTop;
    let left = element.offsetLeft;
    const width = element.offsetWidth;
    const height = element.offsetHeight;

    while(element.offsetParent) {
        element = element.offsetParent;
        top += element.offsetTop;
        left += element.offsetLeft;
    }

    return (
        top < (window.pageYOffset + window.innerHeight)
        && left < (window.pageXOffset + window.innerWidth)
        && (top + height) > window.pageYOffset
        && (left + width) > window.pageXOffset
    );
}

/**
 * Checks for generic "empty" (null, undef, empty str)
 * @param {any} check - argument to check against
 * @returns {boolean}
 */
export function isEmpty(check) {
    return check === null || check === '' || check === undefined;
}

/**
 * Checks for empty object
 * @param {object} object - object to check against
 * @returns {boolean}
 */
export function isEmptyObject(object) {
    if(typeof object === 'undefined' || typeof object !== 'object' || object === null) return true;
    return Object.keys(object).length === 0;
}

/**
 * Detect Mobile/Tablet
 * - we can expand upon this function as needed
 * @returns {boolean}
 */
export function isMobileOrTablet() {
    return (typeof window.orientation !== 'undefined') || (navigator.userAgent.indexOf('IEMobile') !== -1);
}

/**
 * Checks for number
 * @param {any} o - object to check
 * @returns {boolean}
 */
export function isNumber(o) {
    if(o === null) return false;
    return typeof o === 'number' || regexPatterns.regexNumber.test(o);
}

/**
 * Checks if in Edit Mode
 * @returns {boolean}
 */
export function isEditMode() {
    const b = document.getElementsByTagName('body')[0];
    if(b) return b.classList.contains('edit');
    return false;
}

/**
 * Checks if in Preview Mode
 * @returns {boolean}
 */
export function isPreviewMode() {
    const b = document.getElementsByTagName('body')[0];
    if(b) return b.classList.contains('preview');
    return false;
}

/**
 * Parse Hash Params of current URL
 * @returns {object}
 */
export function parseHashParameters() {
    const { hash } = window.location;
    if (hash !== null && hash.length > 0) {
        return parseQueryString(hash.replace('#', ''));
    }

    return {};
}

/**
 * Parse Query String
 * @param {string} queryStr - query string to parse
 * @returns {object}
 */
export function parseQueryString(queryStr) {
    if(typeof queryStr === 'undefined' || typeof queryStr !== 'string' || queryStr === null) return {};

    let regexGroups;
    const urlParams = {};

    if(!isBlank(queryStr)) {
        regexPatterns.regexQueryStrKeyValue.lastIndex = 0;
        while ((regexGroups = regexPatterns.regexQueryStrKeyValue.exec(queryStr))) {
            const key = decodeURIComponent(regexGroups[1].replace(regexPatterns.regexPlus, ' '));
            if (regexGroups[2] !== '') {
                urlParams[key] = decodeURIComponent(regexGroups[3].replace(regexPatterns.regexPlus, ' '));
            } else {
                urlParams[key] = null;
            }
        }
    }

    return urlParams;
}

/**
 * Parse URL
 * @param {string} url - url to parse
 * @returns {object}
 */
export function parseUrl(url) {
    if(typeof url !== 'string' || url === null) return url;

    regexPatterns.regexUrlParse.lastIndex = 0;// Defensive coding to make sure we are starting search at beginning of string
    const pieces = regexPatterns.regexUrlParse.exec(url);
    const keys = ['', 'path', 'scheme', '', '', 'host', 'port', 'relPath', 'queryStringFull', 'queryString', 'hashFull', 'hash'];
    const urlParts = {};
    for (let i = 1; i < 12; i += 1) {
        if (!isEmpty(pieces[i]) && keys[i] !== '') {
            urlParts[keys[i]] = pieces[i];
        }
    }
    if (urlParts.path === null) {
        urlParts.path = '';
    }
    if (urlParts.hash === null && !isEmpty(urlParts.hashFull)) {
        urlParts.hash = '';
    }
    if (urlParts.queryString === null && !isEmpty(urlParts.queryStringFull)) {
        urlParts.queryString = '';
    }
    urlParts.queryParams = parseQueryString(pieces[9]);

    delete urlParts.queryStringFull;
    delete urlParts.hashFull;

    return urlParts;
}

/**
 * Remove Query Param from URL
 * @param {string} url - url to remove query param from
 * @param {string} paramName - param to remove
 * @returns {string} - returns URL string
 */
export function removeQueryParam(url, paramName) {
    if(typeof url !== 'string'
       || (typeof paramName !== 'string' && typeof paramName !== 'number')
    ) return url;

    if (url !== null && paramName !== null) {
        const urlParts = parseUrl(url);

        if (urlParts.queryParams[paramName] !== undefined) {
            delete urlParts.queryParams[paramName];

            let newUrl = url;
            const queryStr = buildQueryString(urlParts.queryParams);
            const newQueryStr = queryStr !== '' ? `?${queryStr}` : '';
            newUrl = `${urlParts.path}${newQueryStr}`;

            if (urlParts.hash !== null) {
                newUrl += `#${urlParts.hash}`;
            }
            return newUrl;
        }
    }
    return url;
}

/**
 * Safe HTML
 * @param {string} val - string value to make safe HTML from
 * @returns {string}
 */
export function safeHtml(val) {
    if(val === null || typeof val !== 'string') return '';
    return val.toString().replace(regexPatterns.safeHtmlRegex, (m, p) => {
        switch (p) {
        case '<':
            return '&lt;';
        case '>':
            return '&gt;';
        case '"':
            return '&quot;';
        case '\'':
            return '&apos;';
        default:
            return '';
        }
    });
}

/**
 * Set Data Attribute on Element
 * @param {DOM Element} element - DOM element to update data attribute on
 * @param {string} dataName - data attr name (ie. 'aria-expanded')
 * @param {string} newProperty - data attr property (ie. 'true')
 */
export function setDataAttr(element, dataName, newProperty) {
    if(typeof element !== 'undefined'
       && (typeof dataName === 'string' || typeof dataName === 'number')
       && (typeof newProperty === 'string' || typeof newProperty === 'number')
       && element !== null
       && newProperty !== null
       && dataName !== null
    ) {
        element.setAttribute(dataName, newProperty);
    }
}

/**
 * Throttle Handler
 * @param {function} callback
 * @param {number} limit
 */
export function throttle(callback, limit = 300) {
    if(typeof callback !== 'function') return false;

    let wait = false;
    return function throttleCallback() {
        if (!wait) {
            callback.call();
            wait = true;
            setTimeout(() => {
                wait = false;
            }, limit);
        }
    };
}

/**
 * Generate a UUID string
 * https://gist.github.com/jed/982883
 * @returns {string}
 */
export function uuid(a) {
    return a?(a^Math.random()*16>>a/4).toString(16):([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,uuid);//eslint-disable-line
}

/**
 * Get Viewport Size
 */
export function getViewportSize(isXL) {
    const maxCheck = isXL ? ROCKWELL_BREAKPOINTS_MAX_EM.XLDESKTOP : ROCKWELL_BREAKPOINTS_MAX_EM.DESKTOP;
    if(window.matchMedia(`(max-width: ${maxCheck}em)`).matches) return 'mobile';
    return 'desktop';
}

/**
 * Detect IE11
 */
export function isIE11() {
    return !!window.MSInputMethodContext && !!document.documentMode;
}

/**
 * Get image ratio reference.
 * If image dimensions match a predefined ratio
 * then add first return value as 'class' attr, e.g.: <img class="${imageRatioRefs.class}">,
 * otherwise add second return value as 'style' attribute, e.g.: <img ${imageRatioRefs.style}>.
 */
export function getImageRatio(width, height) {
    let ratioClass = '';
    let ratioStyle = '';
    const imgRatioVal = width > 0 ? height / width : 0;
    if (imgRatioVal > 0.560 && imgRatioVal < 0.565) {
        ratioClass = '--16x9';
    } else if (imgRatioVal === 1.000) {
        ratioClass = '--1x1';
    } else if (imgRatioVal > 0.816 && imgRatioVal < 0.818) {
        ratioClass = '--104x85';
    } else if (imgRatioVal > 0.814 && imgRatioVal < 0.816) {
        ratioClass = '--27x22';
    } else if (imgRatioVal === 0.750) {
        ratioClass = '--4x3';
    } else if (imgRatioVal > 0.691 && imgRatioVal < 0.696) {
        ratioClass = '--297x206';
    } else if (imgRatioVal > 0.665 && imgRatioVal < 0.670) {
        ratioClass = '--3x2';
    } else {
        ratioStyle = `style="aspect-ratio: ${width}/${height}"`;
    }
    ratioClass = ratioClass !== '' ? ` aspect-ratio${ratioClass}` : '';
    return { class: ratioClass, style: ratioStyle };
}
